import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { Card, ErrorBox } from '../components/bits'

/**
 * The second factor — enrolment and challenge.
 *
 * ## Why this screen exists
 *
 * TOTP was switched on in the Supabase project and then enforced nowhere. No
 * code called `mfa.challenge()`, and no database predicate looked at the JWT's
 * `aal` claim, so `signInWithPassword` returned a fully working `aal1` session
 * even for an account with a factor enrolled. Every `admin_*` RPC accepted it.
 * The portal reported "MFA: enabled" from a column nothing ever wrote.
 *
 * That is worse than having no MFA, because the settings page said otherwise.
 * A Secretariat account can approve members, read every unredacted record —
 * addresses and phone numbers a member has explicitly hidden — and export the
 * whole register. Its only real control was an eight-character password.
 *
 * ## Two halves, and the order matters
 *
 * This component covers the client half: challenge an enrolled user, and let an
 * unenrolled one enrol. On its own that is a UI convention — anyone can skip it
 * by calling `/auth/v1/token` directly and using the raw `aal1` token against
 * PostgREST, never loading this page at all.
 *
 * The server half is `pan_private.is_secretariat()` requiring `aal2`. That is
 * what actually enforces it, and it is deliberately NOT applied yet: turning it
 * on before the staff have enrolled locks every one of them out of the portal
 * they would use to enrol. Enrol first, then enforce. The migration is written
 * and waiting; see 0025 in the repo.
 */

type Stage = 'checking' | 'challenge' | 'enrol' | 'done'

export default function Mfa({ onVerified }: { onVerified: () => void }) {
  const [stage, setStage] = useState<Stage>('checking')
  const [factorId, setFactorId] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const { data: factors, error: listError } = await supabase.auth.mfa.listFactors()
        if (listError) throw listError

        const verified = factors?.totp?.find((f) => f.status === 'verified')
        if (verified) {
          setFactorId(verified.id)
          setStage('challenge')
          return
        }

        // No verified factor. Enrol one — but first clear out any half-finished
        // attempt, because Supabase rejects a second factor with the same
        // friendly name. Without this, abandoning setup once (closing the tab at
        // the QR code) leaves an unverified factor behind and every later
        // attempt fails with "already exists" — locking the account out of the
        // enrolment screen permanently.
        //
        // Read from `all`, not `totp`. The typed shape of listFactors is
        // `{ all: Factor[] } & { [K in FactorType]: Factor<K, 'verified'>[] }` —
        // the per-type arrays contain *verified* factors only, so searching
        // `totp` for an unverified one can never match. It compiled as a silent
        // no-op until the compiler was pointed at it.
        const stale = factors?.all?.find(
          (f) => f.factor_type === 'totp' && f.status === 'unverified',
        )
        if (stale) {
          await supabase.auth.mfa.unenroll({ factorId: stale.id })
        }

        const { data: enrolled, error: enrolError } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: 'Secretariat portal',
        })
        if (enrolError) throw enrolError

        setFactorId(enrolled.id)
        setQr(enrolled.totp.qr_code)
        setSecret(enrolled.totp.secret)
        setStage('enrol')
      } catch (err: unknown) {
        console.error('mfa setup failed', err)
        setError('Could not start two-factor setup. Sign out and try again.')
      }
    })()
  }, [])

  async function verify(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!factorId) return
    setBusy(true)
    setError(null)

    try {
      const { data: challenge, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: code.replace(/\s+/g, ''),
      })

      if (verifyError) {
        // Deliberately not distinguishing "wrong code" from "expired code": both
        // mean try again with the current one, and the difference tells an
        // attacker whether they had the right factor.
        setError('That code was not accepted. Check the current six digits and try again.')
        return
      }

      setStage('done')
      onVerified()
    } catch (err: unknown) {
      console.error('mfa verify failed', err)
      setError('Verification failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function signOut(): Promise<void> {
    await supabase.auth.signOut()
  }

  if (stage === 'checking') {
    return (
      <div className="login-wrap">
        <span className="small muted">Checking two-factor…</span>
      </div>
    )
  }

  return (
    <div className="login-wrap">
      <div className="login-card stack">
        <div style={{ textAlign: 'center' }}>
          <div className="brand-crest" style={{ margin: '0 auto 16px' }}>
            CCP
          </div>
          <p className="kpi-label" style={{ color: 'var(--blue-400)' }}>
            {stage === 'enrol' ? 'Set up two-factor' : 'Two-factor'}
          </p>
          <h1 style={{ marginTop: 6 }}>
            {stage === 'enrol' ? 'Protect this account' : 'Enter your code'}
          </h1>
        </div>

        <Card>
          {stage === 'enrol' && (
            <div className="stack" style={{ gap: 'var(--s-3)' }}>
              <p className="small">
                A Secretariat account can approve members, read every record in
                full and export the register. A password on its own is not
                enough for that. Scan this with an authenticator app — Google
                Authenticator, 1Password, Authy — then enter the six digits it
                shows.
              </p>

              {qr && (
                <div style={{ textAlign: 'center' }}>
                  {/* Supabase returns the QR as an SVG data URL. It is rendered
                      as an image rather than injected as markup so that nothing
                      from the auth response is ever parsed as HTML. */}
                  <img
                    src={qr}
                    alt="QR code for two-factor setup"
                    width={200}
                    height={200}
                    style={{ background: '#fff', borderRadius: 12, padding: 8 }}
                  />
                </div>
              )}

              {secret && (
                <details>
                  <summary className="small">Cannot scan the code?</summary>
                  <p className="small" style={{ marginTop: 8 }}>
                    Enter this key manually:
                  </p>
                  <code className="small" style={{ wordBreak: 'break-all' }}>
                    {secret}
                  </code>
                </details>
              )}
            </div>
          )}

          <form
            onSubmit={(e) => {
              void verify(e)
            }}
            className="stack"
            style={{ gap: 'var(--s-4)', marginTop: 'var(--s-4)' }}
          >
            <div>
              <label className="kpi-label" htmlFor="code">
                Six-digit code
              </label>
              <input
                id="code"
                name="one-time-code"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9 ]*"
                maxLength={7}
                placeholder="123456"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value)
                }}
                required
                autoFocus
              />
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button className="btn btn-primary" type="submit" disabled={busy || code.length < 6}>
              {busy ? 'Checking…' : stage === 'enrol' ? 'Confirm and continue' : 'Verify'}
            </button>
          </form>
        </Card>

        <p className="small muted" style={{ textAlign: 'center' }}>
          <button
            className="btn-link"
            type="button"
            onClick={() => {
              void signOut()
            }}
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  )
}
